import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import VisibilitySensor from 'react-visibility-sensor'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { useLoader } from '@moneymade/moneymade-ui'
import useTracker from 'hooks/useTracker'
import Twitter from 'modules/discover/PlatformPageNew/Twitter/Twitter'
import { toCapitalize } from 'utils'
import styles from './PlatformPageNew.module.scss'

const Snapshot = dynamic(() => import('modules/discover/PlatformPageNew/Snapshot/Snapshot'))
const TopBlock = dynamic(() => import('modules/discover/PlatformPageNew/TopBlock/TopBlockContainer'))
const Overview = dynamic(() => import('modules/discover/PlatformPageNew/Overview/Overview'))
const ProsCons = dynamic(() => import('modules/discover/PlatformPageNew/ProsCons/ProsCons'))
const TrackRecord = dynamic(() => import('modules/discover/PlatformPageNew/TrackRecord/TrackRecord'))
const Calculator = dynamic(() => import('modules/discover/PlatformPageNew/Calculator/Calculator'))
const HowItWorks = dynamic(() => import('modules/discover/PlatformPageNew/HowItWorks/HowItWorks'))
// const SubForm = dynamic(() => import('modules/discover/PlatformPage/SubForm/SubForm'))
const HowMakeMoney = dynamic(() => import('modules/discover/PlatformPageNew/HowMakeMoney/HowMakeMoney'))
const Team = dynamic(() => import('modules/discover/PlatformPageNew/Team/Team'))
const UserReviews = dynamic(() => import('modules/discover/PlatformPageNew/UserReviews/UserReviews'))
const Faq = dynamic(() => import('modules/discover/PlatformPageNew/Faq/Faq'))
const TopPerk = dynamic(() => import('modules/discover/PlatformPageNew/TopPerk/TopPerk'))
const PressCoverage = dynamic(() => import('modules/discover/PlatformPageNew/PressCoverage/PressCoverage'))
const WaysInvest = dynamic(() => import('modules/discover/PlatformPageNew/WaysInvest/WaysInvest'))
const InvestInPlatform = dynamic(() => import('modules/discover/PlatformPageNew/InvestInPlatform/InvestInPlatform'))
const NavigationTop = dynamic(() => import('modules/discover/PlatformPageNew/NavigationTop/NavigationTop'))
const NavigationHorizontal = dynamic(() =>
  import('modules/discover/PlatformPageNew/NavigationHorizontal/NavigationHorizontal')
)

const WEBPARTS = {
  snapshot: 'snapshot',
  topPerk: 'top-perk',
  overview: 'overview',
  prosCons: 'pros-cons',
  trackRecord: 'track-record',
  calculator: 'calculator',
  howItWork: 'how-it-work',
  pressCoverage: 'press-coverage',
  howMakeMoney: 'how-make-money',
  team: 'team',
  investInPlatform: 'invest-in-platform',
  faqs: 'faqs',
  reviews: 'reviews',
  twitter: 'twitter'
}

const PlatformPageNew = ({
  currentInvestment,
  reviews,
  assetPageSlug,
  industriesAvgAll,
  assetCompare,
  industryChange
}) => {
  const {
    name,
    slug,
    website,
    promoVideo,
    brandVideo,
    profile,
    media,
    backgroundColor,
    new_similars: similars,
    investors,
    industries,
    dataIndustry,
    termCalculator,
    logo,
    joinInvestorsText,
    askSlug,
    collapsedDescription,
    totalReviews,
    fees,
    investorsTotal,
    minInvestment,
    averageRating,
    target,
    webparts
  } = currentInvestment
  const { handleUseTracker } = useTracker()
  const { loaderElement } = useLoader(true)
  const { isFallback } = useRouter()
  const navigHorizRef = useRef()
  const topBlock = useRef(null)

  // prepare webparts
  const webpartsList = useMemo(
    () =>
      webparts?.map(({ id, type, titleNavigation, ...rest }, index) => ({
        ...rest,
        id,
        type,
        titleNavigation,
        urlSlug: webparts?.filter(({ type: typeLocal }) => type === typeLocal)?.length > 1 ? `${type}-${index}` : type,
        navLabel: titleNavigation || toCapitalize(type?.replaceAll('-', ' '))
      })) || [],
    [webparts]
  )

  // prepare navigation list
  const navigationList = useMemo(
    () => webpartsList?.filter(({ pickToNavigation }) => pickToNavigation) || [],
    [webpartsList]
  )

  const [stickyHeader, setStickyHeader] = useState(false)
  const [navSelectedId, setSelectedId] = useState(null)
  const [belowNavigationHorizontal, setBelowNavigationHorizontal] = useState(false)

  const handleScroll = useCallback(() => {
    // check if viewport below NavigationHorizontal
    const isBelow = navigHorizRef?.current?.getBoundingClientRect().top < 0
    setBelowNavigationHorizontal(isBelow)
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }, [])

  const handleStickyChange = isVisible => (isVisible ? setStickyHeader(false) : setStickyHeader(true))

  // Send tracking info
  useEffect(() => {
    if (slug) {
      handleUseTracker('PlatformExpandedImpression', 'platform', slug)
    }
  }, [handleUseTracker, slug])

  useEffect(() => {
    if (profile?.verificationStatus === 'Verified Paying (Premium)') {
      const moneyBars = document.querySelectorAll('.money-made-embed[data-embed-widget="money-bar"]')

      const combined = [...moneyBars]
      // eslint-disable-next-line no-restricted-syntax
      for (const element of combined) {
        element.remove()
      }
    }
  }, [profile?.verificationStatus])

  useEffect(() => {
    if (!document.querySelector('[src="https://platform.twitter.com/widgets.js"]')) {
      const script = document.createElement('script')

      script.src = 'https://platform.twitter.com/widgets.js'
      script.charSet = 'utf-8'
      script.async = true

      document.head.appendChild(script)

      return () => {
        document.head.removeChild(script)
      }
    }
  }, [])

  // html content new
  return !isFallback ? (
    <section className={styles.PlatformPageNew}>
      <TopBlock
        name={name}
        logo={logo}
        slug={slug}
        website={website}
        askSlug={askSlug}
        investorsTotal={investorsTotal}
        promoVideo={promoVideo}
        rating={averageRating}
        joinInvestorsText={joinInvestorsText}
        brandVideo={brandVideo}
        verificationStatus={profile?.verificationStatus}
        media={media}
        industry={industries?.[0]}
        backgroundColor={backgroundColor}
        collapsedDescription={collapsedDescription}
        totalReviews={totalReviews}
        assetPageSlug={assetPageSlug}
        topBlock={topBlock}
      />

      <VisibilitySensor offset={{ top: 0 }} onChange={handleStickyChange}>
        <div ref={navigHorizRef}>
          <NavigationHorizontal navigationList={navigationList} handleNavSelectedId={setSelectedId} />{' '}
        </div>
      </VisibilitySensor>

      {stickyHeader && belowNavigationHorizontal ? (
        <NavigationTop
          navigationList={navigationList}
          website={website}
          logo={logo}
          navSelectedId={navSelectedId}
          handleNavSelectedId={setSelectedId}
        />
      ) : null}

      {webpartsList?.length > 0 &&
        webpartsList?.map(webpart => {
          const { type, disable, urlSlug } = webpart

          switch (type) {
            case WEBPARTS.snapshot:
              return !disable ? (
                <Snapshot
                  {...webpart}
                  investors={investors}
                  assetCompare={assetCompare}
                  industryChange={industryChange}
                  key={urlSlug}
                />
              ) : null

            case WEBPARTS.topPerk:
              return !disable ? <TopPerk {...webpart} currentInvestment={currentInvestment} key={urlSlug} /> : null

            case WEBPARTS.overview:
              return !disable ? <Overview {...webpart} key={urlSlug} /> : null

            case WEBPARTS.prosCons:
              return !disable ? <ProsCons {...webpart} key={urlSlug} /> : null

            case WEBPARTS.trackRecord:
              return !disable ? <TrackRecord {...webpart} website={website} key={urlSlug} /> : null

            case WEBPARTS.calculator:
              return !disable ? (
                <Calculator
                  fees={fees}
                  minInvestment={minInvestment}
                  termCalculator={termCalculator}
                  target={target}
                  {...webpart}
                  key={urlSlug}
                />
              ) : null

            case WEBPARTS.howItWork:
              return !disable ? (
                <div key={urlSlug}>
                  <HowItWorks
                    {...webpart}
                    verificationStatus={profile?.verificationStatus}
                    currentInvestment={currentInvestment}
                  />

                  {/* {profile?.verificationStatus ? (
                    <Container width={850} className={styles.ContainerForm}>
                      <SubForm />
                    </Container>
                  ) : null} */}
                </div>
              ) : null

            case WEBPARTS.pressCoverage:
              return !disable ? <PressCoverage {...webpart} key={urlSlug} /> : null

            case WEBPARTS.howMakeMoney:
              return !disable ? <HowMakeMoney {...webpart} key={urlSlug} /> : null

            case WEBPARTS.team:
              return !disable ? <Team {...webpart} key={urlSlug} /> : null

            case WEBPARTS.reviews:
              return !disable ? (
                <UserReviews
                  currentInvestment={currentInvestment}
                  reviews={reviews}
                  rating={averageRating}
                  {...webpart}
                  key={urlSlug}
                />
              ) : null

            case WEBPARTS.investInPlatform:
              return !disable ? (
                <InvestInPlatform
                  {...webpart}
                  currentInvestment={currentInvestment}
                  assetPageSlug={assetPageSlug}
                  rating={averageRating}
                  key={urlSlug}
                />
              ) : null

            case WEBPARTS.twitter:
              return !disable ? <Twitter {...webpart} key={urlSlug} /> : null

            case WEBPARTS.faqs:
              return !disable ? <Faq {...webpart} key={urlSlug} /> : null

            default:
              return null
          }
        })}

      {similars?.length && profile?.verificationStatus !== 'Verified Paying (Premium)' ? (
        <WaysInvest industryName={dataIndustry?.name} similars={similars} industriesAvgAll={industriesAvgAll} />
      ) : null}
    </section>
  ) : (
    <div className={styles.Loading}>{loaderElement}</div>
  )
}

PlatformPageNew.propTypes = {
  currentInvestment: PropTypes.shape({
    name: PropTypes.string,
    new_similars: PropTypes.arrayOf(PropTypes.object),
    slug: PropTypes.string,
    website: PropTypes.string,
    promoVideo: PropTypes.string,
    brandVideo: PropTypes.string,
    media: PropTypes.arrayOf(PropTypes.string),
    backgroundColor: PropTypes.string,
    joinInvestorsText: PropTypes.string,
    investors: PropTypes.string,
    industries: PropTypes.arrayOf(PropTypes.object),
    dataIndustry: PropTypes.shape({ name: PropTypes.string }),
    logo: PropTypes.string,
    profile: PropTypes.shape({
      verificationStatus: PropTypes.string
    }),
    askSlug: PropTypes.string,
    collapsedDescription: PropTypes.string,
    termCalculator: PropTypes.number,
    totalReviews: PropTypes.number,
    fees: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    investorsTotal: PropTypes.number,
    minInvestment: PropTypes.number,
    averageRating: PropTypes.number,
    target: PropTypes.shape({
      max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      min: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    webparts: PropTypes.arrayOf(PropTypes.object)
  }),
  reviews: PropTypes.arrayOf(PropTypes.object),
  assetPageSlug: PropTypes.string,
  topIndustry: PropTypes.arrayOf(PropTypes.object),
  assetCompare: PropTypes.shape({}),
  isAuthChecked: PropTypes.bool,
  industriesAvgAll: PropTypes.shape({}),
  isLogged: PropTypes.bool,
  industryChange: PropTypes.string
}

PlatformPageNew.defaultProps = {
  currentInvestment: {
    name: '',
    similars: [],
    slug: '',
    profile: {},
    investors: '',
    promoVideo: '',
    joinInvestorsText: '',
    brandVideo: '',
    media: [],
    backgroundColor: '',
    industries: [],
    dataIndustry: {},
    termCalculator: 0,
    logo: '',
    askSlug: '',
    collapsedDescription: '',
    totalReviews: 0,
    fees: '',
    investorsTotal: 0,
    minInvestment: 0,
    averageRating: 0,
    target: {},
    webparts: []
  },
  reviews: [],
  assetPageSlug: '',
  topIndustry: [],
  assetCompare: {},
  industriesAvgAll: {},
  isAuthChecked: false,
  isLogged: false,
  industryChange: ''
}

export default PlatformPageNew
